import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiFilter,
  FiHome,
} from "react-icons/fi";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { getCurrencyMinorUnits } from "../currency/currencyPrecision";
import { createReservationTranslator, getReservationLocale } from "./reservationI18n";
import { getAccountLineDescription, reservationAccountMessages } from "./reservationAccountMessages";
import "./ReservationAccountView.css";
import {
  STATE_OPTIONS,
  calculateQuarantinedCharges,
  getLineStateKey,
  getLineStateMeta,
} from "./reservationAccountState";

const LEDGER_FAMILIES = [
  { key: "room", label: "Room charges" },
  { key: "extras", label: "Extras" },
  { key: "charges", label: "Charges" },
  { key: "payments", label: "Payments" },
];

const AMOUNT_OPERATORS = [
  { key: "none", label: "No amount filter" },
  { key: "eq", label: "Equals" },
  { key: "gt", label: "Greater than" },
  { key: "gte", label: "Greater or equal" },
  { key: "lt", label: "Less than" },
  { key: "lte", label: "Less or equal" },
  { key: "between", label: "Between" },
];

const DATE_UNSCHEDULED_KEY = "__unscheduled__";
const defaultTranslator = createReservationTranslator("en");

function createDefaultColumnFilters() {
  return {
    date: { selected: [], from: "", to: "" },
    reservation: { selected: [] },
    roomState: { selected: [] },
    roomConcept: { selected: [], match: "contains", query: "" },
    roomAmount: { selected: [], operator: "none", value: "", valueTo: "" },
    extrasState: { selected: [] },
    extrasConcept: { selected: [], match: "contains", query: "" },
    extrasAmount: { selected: [], operator: "none", value: "", valueTo: "" },
    chargesState: { selected: [] },
    chargesConcept: { selected: [], match: "contains", query: "" },
    chargesAmount: { selected: [], operator: "none", value: "", valueTo: "" },
    paymentsState: { selected: [] },
    paymentsConcept: { selected: [], match: "contains", query: "" },
    paymentsAmount: { selected: [], operator: "none", value: "", valueTo: "" },
  };
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatAccountMoney(value, currency = "EUR", currencyCatalog = [], locale = "en-GB") {
  const minorUnits = getCurrencyMinorUnits(currency, currencyCatalog);
  return `${toNumber(value).toLocaleString(locale, {
    minimumFractionDigits: minorUnits,
    maximumFractionDigits: minorUnits,
  })} ${currency}`;
}

export function formatAccountSignedMoney(value, currency = "EUR", currencyCatalog = [], locale = "en-GB") {
  const amount = toNumber(value);
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  const minorUnits = getCurrencyMinorUnits(currency, currencyCatalog);
  return `${sign}${Math.abs(amount).toLocaleString(locale, {
    minimumFractionDigits: minorUnits,
    maximumFractionDigits: minorUnits,
  })} ${currency}`;
}

function dateLabel(value, t) {
  if (!value) return t("Unscheduled");
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function buildReservationOptionLabel(reservation, t) {
  const label = reservation?.reservation_label || reservation?.locator;
  const generatedLabel = !reservation?.locator && /^Reservation (\d+)$/.exec(label || "");
  const chunks = [
    generatedLabel
      ? t("Reservation {id}", { id: generatedLabel[1] })
      : label || t("Reservation {id}", { id: reservation?.id }),
    reservation?.guest_name,
    reservation?.room_number ? t("Room {number}", { number: reservation.room_number }) : null,
  ].filter(Boolean);
  return chunks.join(" · ");
}

function buildReservationShortLabel(reservation) {
  const locator = String(reservation?.locator || reservation?.reservation_label || "").trim();
  const locatorMatch = locator.match(/-(\d+)$/);
  if (locatorMatch) return locatorMatch[1];

  const groupIndex = Number(reservation?.group_index);
  if (Number.isFinite(groupIndex)) return String(groupIndex + 1);

  return reservation?.id ? String(reservation.id) : "-";
}

function buildReservationTooltip(reservation, t) {
  const shortLabel = buildReservationShortLabel(reservation);
  return [
    t("Reservation {id}", { id: shortLabel }),
    reservation?.locator ? t("Locator: {locator}", { locator: reservation.locator }) : null,
    reservation?.guest_name ? t("Main guest: {name}", { name: reservation.guest_name }) : null,
    reservation?.room_number ? t("Room: {number}", { number: reservation.room_number }) : null,
    reservation?.room_category_name ? t("Category: {name}", { name: reservation.room_category_name }) : null,
  ]
    .filter(Boolean)
    .join("\n");
}

function buildLineReservationBadge(line, reservationsById, t = defaultTranslator) {
  const reservationId = Number(line.reservation_id);
  if (Number.isFinite(reservationId) && reservationId > 0) {
    const reservation = reservationsById.get(reservationId) || {
      id: reservationId,
      locator: line.reservation_locator,
      reservation_label: line.reservation_label,
      guest_name: line.guest_name,
      room_number: line.room_number,
      room_category_name: line.room_category_name,
    };
    return {
      label: buildReservationShortLabel(reservation),
      title: buildReservationTooltip(reservation, t),
    };
  }

  if (line.scope === "unallocated") {
    return { label: "U", title: t("Unallocated payment") };
  }

  if (line.scope === "group") {
    return { label: "G", title: t("Group-level line") };
  }

  return { label: "-", title: "" };
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

export function normalizeAccountAmountKey(value, currency = "EUR", currencyCatalog = []) {
  return toNumber(value).toFixed(getCurrencyMinorUnits(currency, currencyCatalog));
}

function parseAmountValue(value) {
  const normalized = String(value || "").replace(",", ".").trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function getDateFilterKey(line) {
  return line.date ? String(line.date) : DATE_UNSCHEDULED_KEY;
}

function getReservationFilterKey(line, reservationsById) {
  return buildLineReservationBadge(line, reservationsById).label;
}

function getConceptFilterKey(familyKey) {
  return `${familyKey}Concept`;
}

function getStateFilterKey(familyKey) {
  return `${familyKey}State`;
}

function getAmountFilterKey(familyKey) {
  return `${familyKey}Amount`;
}

function getConceptValue(line, familyKey) {
  return line.column === familyKey ? String(line.description || "") : "";
}

function getAmountValue(line, familyKey) {
  return line.column === familyKey ? getLedgerAmount(line) : null;
}

function isDateFilterActive(filter) {
  return Boolean(filter?.selected?.length || filter?.from || filter?.to);
}

function isSelectionFilterActive(filter) {
  return Boolean(filter?.selected?.length);
}

function isConceptFilterActive(filter) {
  return Boolean(filter?.selected?.length || String(filter?.query || "").trim());
}

function isAmountFilterActive(filter) {
  return Boolean(
    filter?.selected?.length ||
      (filter?.operator && filter.operator !== "none" && String(filter.value || "").trim())
  );
}

function isColumnFilterActive(filters, key) {
  if (key === "date") return isDateFilterActive(filters.date);
  if (key === "reservation") return isSelectionFilterActive(filters.reservation);
  if (key.endsWith("State")) return isSelectionFilterActive(filters[key]);
  if (key.endsWith("Concept")) return isConceptFilterActive(filters[key]);
  if (key.endsWith("Amount")) return isAmountFilterActive(filters[key]);
  return false;
}

function hasActiveColumnFilters(filters) {
  return Object.keys(filters).some((key) => isColumnFilterActive(filters, key));
}

function compareAmount(amount, filter, equalityTolerance = 0.005) {
  if (!filter?.operator || filter.operator === "none") return true;

  const value = parseAmountValue(filter.value);
  const valueTo = parseAmountValue(filter.valueTo);
  if (value === null) return true;

  switch (filter.operator) {
    case "eq":
      return Math.abs(amount - value) < equalityTolerance;
    case "gt":
      return amount > value;
    case "gte":
      return amount >= value;
    case "lt":
      return amount < value;
    case "lte":
      return amount <= value;
    case "between": {
      const low = Math.min(value, valueTo ?? value);
      const high = Math.max(value, valueTo ?? value);
      return amount >= low && amount <= high;
    }
    default:
      return true;
  }
}

function lineMatchesDateFilter(line, filter) {
  if (!isDateFilterActive(filter)) return true;

  const key = getDateFilterKey(line);
  const hasSelectedDates = Boolean(filter.selected?.length);
  const hasRange = Boolean(filter.from || filter.to);
  const matchesSelected = hasSelectedDates && filter.selected.includes(key);
  const matchesRange =
    hasRange &&
    line.date &&
    (!filter.from || String(line.date) >= filter.from) &&
    (!filter.to || String(line.date) <= filter.to);

  return matchesSelected || matchesRange;
}

function lineMatchesSelectionFilter(value, filter) {
  if (!isSelectionFilterActive(filter)) return true;
  return filter.selected.includes(value);
}

function lineMatchesStateFilter(line, familyKey, filter) {
  if (!isSelectionFilterActive(filter)) return true;
  if (line.column !== familyKey) return false;
  return filter.selected.includes(getLineStateKey(line));
}

function lineMatchesConceptFilter(line, familyKey, filter, t) {
  if (!isConceptFilterActive(filter)) return true;
  if (line.column !== familyKey) return false;

  const value = getConceptValue(line, familyKey);
  const selectedMatch = filter.selected?.includes(value);
  const query = normalizeText(filter.query);

  if (!query) return Boolean(selectedMatch);

  const normalizedValue = normalizeText(getAccountLineDescription(line, t));
  const queryMatch =
    filter.match === "exact" ? normalizedValue === query : normalizedValue.includes(query);
  return Boolean(selectedMatch || queryMatch);
}

function lineMatchesAmountFilter(
  line,
  familyKey,
  filter,
  {
    convertAmount = (value) => value,
    normalizeAmount = (value) => normalizeAccountAmountKey(value),
    equalityTolerance = 0.005,
  } = {}
) {
  if (!isAmountFilterActive(filter)) return true;
  if (line.column !== familyKey) return false;

  const baseAmount = getAmountValue(line, familyKey);
  if (baseAmount === null) return false;
  const amount = convertAmount(baseAmount);

  const selectedMatch = filter.selected?.includes(normalizeAmount(amount));
  const comparisonMatch =
    filter.operator && filter.operator !== "none" && String(filter.value || "").trim()
      ? compareAmount(amount, filter, equalityTolerance)
      : false;

  return Boolean(selectedMatch || comparisonMatch);
}

function calculateLineTotals(lines) {
  let postedCharges = 0;
  let scheduledCharges = 0;
  let unpostedCharges = 0;
  let payments = 0;

  lines.forEach((line) => {
    if (!line.is_counted) return;

    const signedAmount = toNumber(line.signed_amount);
    if (line.column === "payments") {
      payments += -signedAmount;
      return;
    }

    const state = getLineStateKey(line);
    if (state === "posted") {
      postedCharges += signedAmount;
    } else if (state === "scheduled") {
      scheduledCharges += signedAmount;
    } else if (state === "unposted") {
      unpostedCharges += signedAmount;
    }
  });

  const balanceToday = postedCharges - payments;
  const projectedBalance = postedCharges + unpostedCharges + scheduledCharges - payments;

  return {
    posted_charges: postedCharges,
    scheduled_charges: scheduledCharges,
    unposted_charges: unpostedCharges,
    payments,
    balance_today: balanceToday,
    projected_balance: projectedBalance,
    credit_refundable: Math.max(-projectedBalance, 0),
  };
}


function ColumnFilterCell({ label, filterKey, active, isOpen, alignRight, onToggle, onClear, children, t }) {
  return (
    <div className={`rav-th rav-filterTh${active ? " rav-filterTh--active" : ""}`}>
      <button
        type="button"
        className="rav-columnFilterTrigger"
        onClick={() => onToggle(filterKey)}
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        <FiFilter size={12} aria-hidden="true" />
      </button>
      {isOpen ? (
        <div className={`rav-filterPanel${alignRight ? " rav-filterPanel--right" : ""}`}>
          <div className="rav-filterPanelTitle">{label}</div>
          {children}
          <div className="rav-filterActions">
            <button type="button" className="rav-filterBtn" onClick={() => onClear(filterKey)} disabled={!active}>
              {t("Clear")}
            </button>
            <button type="button" className="rav-filterBtn rav-filterBtn--active" onClick={() => onToggle(null)}>
              {t("Done")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CheckboxOptionList({ options, selected, onToggle, t }) {
  if (!options.length) {
    return <div className="rav-filterEmpty">{t("No values")}</div>;
  }

  return (
    <div className="rav-filterOptions">
      {options.map((option) => (
        <label key={option.key} className="rav-filterOption" title={option.title || option.label}>
          <input
            type="checkbox"
            checked={selected.includes(option.key)}
            onChange={() => onToggle(option.key)}
          />
          <span className="rav-filterOptionText">{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function DateFilterPanel({ filter, options, onToggle, onPatch, t }) {
  return (
    <>
      <div className="rav-filterPanelSection">
        <CheckboxOptionList options={options} selected={filter.selected} onToggle={onToggle} t={t} />
      </div>
      <div className="rav-filterPanelSection">
        <div className="rav-filterInline">
          <input
            type="date"
            aria-label={t("From date")}
            className="rav-filterField"
            value={filter.from}
            onChange={(event) => onPatch({ from: event.target.value })}
          />
          <input
            type="date"
            aria-label={t("To date")}
            className="rav-filterField"
            value={filter.to}
            onChange={(event) => onPatch({ to: event.target.value })}
          />
        </div>
      </div>
    </>
  );
}

function SelectionFilterPanel({ filter, options, onToggle, t }) {
  return (
    <div className="rav-filterPanelSection">
      <CheckboxOptionList options={options} selected={filter.selected} onToggle={onToggle} t={t} />
    </div>
  );
}

function ConceptFilterPanel({ filter, options, onToggle, onPatch, t }) {
  return (
    <>
      <div className="rav-filterPanelSection">
        <CheckboxOptionList options={options} selected={filter.selected} onToggle={onToggle} t={t} />
      </div>
      <div className="rav-filterPanelSection">
        <select
          className="rav-filterMiniSelect"
          aria-label={t("Concept match")}
          value={filter.match}
          onChange={(event) => onPatch({ match: event.target.value })}
        >
          <option value="contains">{t("Contains")}</option>
          <option value="exact">{t("Exact")}</option>
        </select>
        <input
          type="text"
          className="rav-filterField"
          value={filter.query}
          onChange={(event) => onPatch({ query: event.target.value })}
          placeholder={t("Concept")}
          aria-label={t("Concept")}
        />
      </div>
    </>
  );
}

function AmountFilterPanel({ filter, options, onToggle, onPatch, minorUnits = 2, t }) {
  const needsSecondValue = filter.operator === "between";
  return (
    <>
      <div className="rav-filterPanelSection">
        <CheckboxOptionList options={options} selected={filter.selected} onToggle={onToggle} t={t} />
      </div>
      <div className="rav-filterPanelSection">
        <select
          className="rav-filterMiniSelect"
          aria-label={t("Amount condition")}
          value={filter.operator}
          onChange={(event) => onPatch({ operator: event.target.value })}
        >
          {AMOUNT_OPERATORS.map((operator) => (
            <option key={operator.key} value={operator.key}>
              {t(operator.label)}
            </option>
          ))}
        </select>
        <div className={needsSecondValue ? "rav-filterInline" : ""}>
          <input
            type="text"
            inputMode={minorUnits === 0 ? "numeric" : "decimal"}
            className="rav-filterField"
            value={filter.value}
            onChange={(event) => onPatch({ value: event.target.value })}
            placeholder={t("Amount")}
            aria-label={t("Amount")}
          />
          {needsSecondValue ? (
            <input
              type="text"
              inputMode={minorUnits === 0 ? "numeric" : "decimal"}
              className="rav-filterField"
              value={filter.valueTo}
              onChange={(event) => onPatch({ valueTo: event.target.value })}
              placeholder={t("To")}
              aria-label={t("To")}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

function getLedgerAmount(line) {
  return toNumber(line.signed_amount);
}

function LedgerRow({ line, currency, reservationsById, formatDisplayMoney, formatDisplaySignedMoney, t }) {
  const badge = buildLineReservationBadge(line, reservationsById, t);
  const state = getLineStateMeta(line);
  const lineCurrency = line.currency || currency;
  const description = getAccountLineDescription(line, t);

  const renderConceptCell = (familyKey) => (
    <div className="rav-ledgerCell">
      {line.column === familyKey ? (
        <span className="rav-conceptText" title={description}>
          {description}
        </span>
      ) : (
        <span aria-hidden="true" />
      )}
    </div>
  );

  const renderAmountCell = (familyKey) => (
    <div className="rav-ledgerCell rav-amountCell">
      {line.column === familyKey ? (
        familyKey === "payments"
          ? formatDisplaySignedMoney(getLedgerAmount(line), lineCurrency)
          : formatDisplayMoney(getLedgerAmount(line), lineCurrency)
      ) : (
        <span aria-hidden="true" />
      )}
    </div>
  );

  const renderStateCell = (familyKey) => (
    <div className="rav-ledgerCell rav-familyStateCell">
      {line.column === familyKey ? (
        <span className={`rav-statePill ${state.className}`}>{t(state.label)}</span>
      ) : (
        <span aria-hidden="true" />
      )}
    </div>
  );

  return (
    <div className={`rav-ledgerRow${line.is_counted ? "" : " rav-ledgerRow--muted"}`}>
      <div className="rav-ledgerCell">{dateLabel(line.date, t)}</div>
      <div className="rav-ledgerCell">
        <span className="rav-reservationBadge" title={badge.title}>
          {badge.label}
        </span>
      </div>
      {LEDGER_FAMILIES.flatMap((family) => [
        <React.Fragment key={`${family.key}-concept`}>
          {renderConceptCell(family.key)}
        </React.Fragment>,
        <React.Fragment key={`${family.key}-state`}>
          {renderStateCell(family.key)}
        </React.Fragment>,
        <React.Fragment key={`${family.key}-amount`}>
          {renderAmountCell(family.key)}
        </React.Fragment>,
      ])}
    </div>
  );
}

function ReservationAccountView() {
  const { reservation_group_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { themeId, languageId } = usePmsTheme();
  const t = useMemo(() => createReservationTranslator(languageId, reservationAccountMessages), [languageId]);
  const locale = getReservationLocale(languageId);
  const {
    enabled: multiCurrencyEnabled,
    displayCurrency,
    convertFromBase,
    currencyCatalog,
  } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("palantir_user") || "null"),
    []
  );
  const businessUnitId =
    searchParams.get("business_unit_id") ||
    currentUser?.active_business_unit_id ||
    "1";
  const reservationIdFromUrl = Number(searchParams.get("reservation_id"));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountMode, setAccountMode] = useState(
    Number.isFinite(reservationIdFromUrl) && reservationIdFromUrl > 0
      ? "reservation"
      : "group"
  );
  const [selectedReservationId, setSelectedReservationId] = useState(
    Number.isFinite(reservationIdFromUrl) && reservationIdFromUrl > 0
      ? reservationIdFromUrl
      : null
  );
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [columnFilters, setColumnFilters] = useState(createDefaultColumnFilters);

  const loadAccount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: payload } = await axios.get(
        `/api/reservation-groups/${reservation_group_id}/account`
      );
      setData(payload || null);
    } catch (err) {
      console.error("[ReservationAccountView][LOAD][ERR]", err?.response?.data || err);
      setError(err?.response?.data?.error || "Failed to load account.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [reservation_group_id]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (!data?.reservations?.length) return;
    const urlReservationId = Number(searchParams.get("reservation_id"));
    if (Number.isFinite(urlReservationId) && urlReservationId > 0) {
      setAccountMode("reservation");
      setSelectedReservationId(urlReservationId);
      return;
    }

    if (!selectedReservationId) {
      setSelectedReservationId(Number(data.reservations[0].id));
    }
  }, [data, searchParams, selectedReservationId]);

  const baseCurrency = data?.currency || "EUR";
  const currency = multiCurrencyEnabled ? displayCurrency : baseCurrency;
  const displayMinorUnits = getCurrencyMinorUnits(currency, currencyCatalog);
  const convertAmountForDisplay = useCallback(
    (value) => multiCurrencyEnabled ? convertFromBase(value) : value,
    [convertFromBase, multiCurrencyEnabled]
  );
  const formatDisplayMoney = useCallback(
    (value) => formatAccountMoney(convertAmountForDisplay(value), currency, currencyCatalog, locale),
    [convertAmountForDisplay, currency, currencyCatalog, locale]
  );
  const formatDisplaySignedMoney = useCallback(
    (value) => formatAccountSignedMoney(convertAmountForDisplay(value), currency, currencyCatalog, locale),
    [convertAmountForDisplay, currency, currencyCatalog, locale]
  );
  const normalizeDisplayAmount = useCallback(
    (value) => normalizeAccountAmountKey(value, currency, currencyCatalog),
    [currency, currencyCatalog]
  );
  const reservations = useMemo(() => data?.reservations || [], [data?.reservations]);
  const reservationsById = useMemo(() => {
    const map = new Map();
    reservations.forEach((reservation) => {
      map.set(Number(reservation.id), reservation);
    });
    return map;
  }, [reservations]);
  const selectedReservation = reservations.find(
    (reservation) => Number(reservation.id) === Number(selectedReservationId)
  );

  const updateReservationQuery = (reservationId) => {
    const next = new URLSearchParams(searchParams);
    next.set("business_unit_id", businessUnitId);
    if (reservationId) next.set("reservation_id", String(reservationId));
    else next.delete("reservation_id");
    setSearchParams(next, { replace: true });
  };

  const openGroupAccount = () => {
    setAccountMode("group");
    updateReservationQuery(null);
  };

  const openReservationAccount = (reservationId = selectedReservationId) => {
    const resolvedId = Number(reservationId || reservations[0]?.id);
    if (!Number.isFinite(resolvedId) || resolvedId <= 0) return;
    setAccountMode("reservation");
    setSelectedReservationId(resolvedId);
    updateReservationQuery(resolvedId);
  };

  const baseLines = useMemo(() => {
    let lines = Array.isArray(data?.lines) ? data.lines : [];

    if (accountMode === "reservation") {
      lines = lines.filter((line) => Number(line.reservation_id) === Number(selectedReservationId));
    }

    return lines;
  }, [accountMode, data, selectedReservationId]);

  const dateOptions = useMemo(() => {
    const map = new Map();
    baseLines.forEach((line) => {
      const key = getDateFilterKey(line);
      map.set(key, {
        key,
        label: key === DATE_UNSCHEDULED_KEY ? t("Unscheduled") : dateLabel(key, t),
      });
    });

    return [...map.values()].sort((a, b) => {
      if (a.key === DATE_UNSCHEDULED_KEY) return 1;
      if (b.key === DATE_UNSCHEDULED_KEY) return -1;
      return a.key.localeCompare(b.key);
    });
  }, [baseLines, t]);

  const reservationOptions = useMemo(() => {
    const map = new Map();
    baseLines.forEach((line) => {
      const badge = buildLineReservationBadge(line, reservationsById, t);
      map.set(badge.label, {
        key: badge.label,
        label: badge.label,
        title: badge.title,
      });
    });

    return [...map.values()].sort((a, b) => String(a.key).localeCompare(String(b.key)));
  }, [baseLines, reservationsById, t]);

  const stateOptionsByFamily = useMemo(() => {
    const result = {};
    LEDGER_FAMILIES.forEach((family) => {
      const present = new Set(
        baseLines
          .filter((line) => line.column === family.key)
          .map((line) => getLineStateKey(line))
      );
      result[family.key] = STATE_OPTIONS.filter((option) => present.has(option.key))
        .map((option) => ({ ...option, label: t(option.label) }));
    });
    return result;
  }, [baseLines, t]);

  const conceptOptionsByFamily = useMemo(() => {
    const result = {};
    LEDGER_FAMILIES.forEach((family) => {
      const map = new Map();
      baseLines
        .filter((line) => line.column === family.key)
        .forEach((line) => {
          const value = getConceptValue(line, family.key);
          if (value) {
            map.set(value, { key: value, label: getAccountLineDescription(line, t) });
          }
        });
      result[family.key] = [...map.values()].sort((a, b) => a.label.localeCompare(b.label, locale));
    });
    return result;
  }, [baseLines, locale, t]);

  const amountOptionsByFamily = useMemo(() => {
    const result = {};
    LEDGER_FAMILIES.forEach((family) => {
      const map = new Map();
      baseLines
        .filter((line) => line.column === family.key)
        .forEach((line) => {
          const amount = getLedgerAmount(line);
          const key = normalizeDisplayAmount(convertAmountForDisplay(amount));
          map.set(key, {
            key,
            label:
              family.key === "payments"
                ? formatDisplaySignedMoney(amount)
                : formatDisplayMoney(amount),
          });
        });
      result[family.key] = [...map.values()].sort((a, b) => Number(a.key) - Number(b.key));
    });
    return result;
  }, [baseLines, convertAmountForDisplay, formatDisplayMoney, formatDisplaySignedMoney, normalizeDisplayAmount]);

  const patchColumnFilter = (filterKey, patch) => {
    setColumnFilters((current) => ({
      ...current,
      [filterKey]: {
        ...current[filterKey],
        ...patch,
      },
    }));
  };

  const toggleColumnSelection = (filterKey, value) => {
    setColumnFilters((current) => {
      const selected = current[filterKey]?.selected || [];
      const nextSelected = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
      return {
        ...current,
        [filterKey]: {
          ...current[filterKey],
          selected: nextSelected,
        },
      };
    });
  };

  const clearColumnFilter = (filterKey) => {
    const defaults = createDefaultColumnFilters();
    setColumnFilters((current) => ({
      ...current,
      [filterKey]: defaults[filterKey],
    }));
  };

  const clearAllColumnFilters = () => {
    setColumnFilters(createDefaultColumnFilters());
  };

  const filteredLines = useMemo(() => {
    let lines = baseLines.filter((line) => {
      if (!lineMatchesDateFilter(line, columnFilters.date)) return false;
      if (!lineMatchesSelectionFilter(getReservationFilterKey(line, reservationsById), columnFilters.reservation)) {
        return false;
      }
      return LEDGER_FAMILIES.every((family) => {
        const stateFilter = columnFilters[getStateFilterKey(family.key)];
        const conceptFilter = columnFilters[getConceptFilterKey(family.key)];
        const amountFilter = columnFilters[getAmountFilterKey(family.key)];
        return (
          lineMatchesStateFilter(line, family.key, stateFilter) &&
          lineMatchesConceptFilter(line, family.key, conceptFilter, t) &&
          lineMatchesAmountFilter(line, family.key, amountFilter, {
            convertAmount: convertAmountForDisplay,
            normalizeAmount: normalizeDisplayAmount,
            equalityTolerance: 0.5 / (10 ** displayMinorUnits),
          })
        );
      });
    });

    return [...lines].sort((a, b) => {
      const dateA = a.date || "9999-12-31";
      const dateB = b.date || "9999-12-31";
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const resA = Number(a.reservation_id || 0);
      const resB = Number(b.reservation_id || 0);
      if (resA !== resB) return resA - resB;
      if (a.column !== b.column) return String(a.column).localeCompare(String(b.column));
      return String(a.id).localeCompare(String(b.id));
    });
  }, [
    baseLines,
    columnFilters,
    convertAmountForDisplay,
    displayMinorUnits,
    normalizeDisplayAmount,
    reservationsById,
    t,
  ]);

  const backToReservationGroup = () => {
    navigate(`/reservation-group/${reservation_group_id}?mode=view&business_unit_id=${businessUnitId}`);
  };

  const totals = useMemo(() => calculateLineTotals(filteredLines), [filteredLines]);
  const quarantinedCharges = useMemo(
    () => calculateQuarantinedCharges(filteredLines),
    [filteredLines]
  );
  const hasColumnFilters = useMemo(() => hasActiveColumnFilters(columnFilters), [columnFilters]);
  const toggleFilterPanel = (filterKey) => {
    setOpenFilterKey((current) => (filterKey && current !== filterKey ? filterKey : null));
  };
  const pageTitle = t(accountMode === "reservation" ? "Reservation Account" : "Group Account");
  const locator = data?.group?.group_locator || t("Group {id}", { id: reservation_group_id });

  return (
    <div className={`rav-page${isLightTheme ? " rav-page--light" : ""}`}>
      <div className="rav-shell">
        <div className="rav-header">
          <div>
            <div className="rav-kicker">
              <FiFileText size={16} aria-hidden="true" />
              {t("Account")}
            </div>
            <h1 className="rav-title">{pageTitle}</h1>
            <div className="rav-subtitle">
              {locator}
              {selectedReservation && accountMode === "reservation"
                ? ` · ${buildReservationOptionLabel(selectedReservation, t)}`
                : ""}
            </div>
          </div>

          <div className="rav-actions">
            <button type="button" className="rav-btn rav-btn--primary" onClick={backToReservationGroup}>
              <FiArrowLeft size={15} aria-hidden="true" />
              {t("Back to Reservation Group")}
            </button>
          </div>
        </div>

        <div className="rav-tabs" role="tablist" aria-label={t("Account view")}>
          <button
            type="button"
            className={`rav-tab${accountMode === "group" ? " rav-tab--active" : ""}`}
            onClick={openGroupAccount}
          >
            <FiHome size={15} aria-hidden="true" /> {t("Group Account")}
          </button>
          <button
            type="button"
            className={`rav-tab${accountMode === "reservation" ? " rav-tab--active" : ""}`}
            onClick={() => openReservationAccount()}
            disabled={!reservations.length}
          >
            <FiCreditCard size={15} aria-hidden="true" /> {t("Reservation Account")}
          </button>
        </div>

        {loading ? (
          <div className="rav-state">{t("Loading account...")}</div>
        ) : error ? (
          <div className="rav-state">{t(error)}</div>
        ) : !data ? (
          <div className="rav-state">{t("No account data available.")}</div>
        ) : (
          <>
            <div className="rav-totals">
              <div className="rav-total">
                <div className="rav-totalLabel">{t("Posted revenue")}</div>
                <div className="rav-totalValue">{formatDisplayMoney(totals.posted_charges)}</div>
              </div>
              <div className="rav-total">
                <div className="rav-totalLabel">{t("Scheduled revenue")}</div>
                <div className="rav-totalValue">{formatDisplayMoney(totals.scheduled_charges)}</div>
              </div>
              <div className="rav-total">
                <div className="rav-totalLabel">{t("Unposted past")}</div>
                <div className="rav-totalValue">{formatDisplayMoney(totals.unposted_charges)}</div>
              </div>
              {quarantinedCharges > 0 ? (
                <div className="rav-total">
                  <div className="rav-totalLabel">{t("Quarantined revenue")}</div>
                  <div className="rav-totalValue">{formatDisplayMoney(quarantinedCharges)}</div>
                </div>
              ) : null}
              <div className="rav-total">
                <div className="rav-totalLabel">{t("Payments")}</div>
                <div className="rav-totalValue">{formatDisplayMoney(totals.payments)}</div>
              </div>
              <div className="rav-total rav-total--balance">
                <div className="rav-totalLabel">{t("Balance today")}</div>
                <div className="rav-totalValue">{formatDisplaySignedMoney(totals.balance_today)}</div>
              </div>
              <div className="rav-total rav-total--balance">
                <div className="rav-totalLabel">{t("Projected balance")}</div>
                <div className="rav-totalValue">{formatDisplaySignedMoney(totals.projected_balance)}</div>
              </div>
              <div className="rav-total rav-total--credit">
                <div className="rav-totalLabel">{t("Credit/refundable")}</div>
                <div className="rav-totalValue">{formatDisplayMoney(totals.credit_refundable)}</div>
              </div>
            </div>

            <div className="rav-controls">
              {accountMode === "reservation" ? (
                <select
                  className="rav-select"
                  aria-label={t("Select reservation")}
                  value={selectedReservationId || ""}
                  onChange={(event) => {
                    const nextId = Number(event.target.value);
                    setSelectedReservationId(nextId);
                    updateReservationQuery(nextId);
                  }}
                  disabled={!reservations.length}
                >
                  {reservations.map((reservation) => (
                    <option key={reservation.id} value={reservation.id}>
                      {buildReservationOptionLabel(reservation, t)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="rav-contextNote">{t("Showing group-level, reservation and unallocated lines.")}</div>
              )}

              <div className="rav-filterGroup">
                {accountMode === "group" ? (
                  <button
                    type="button"
                    className="rav-btn"
                    onClick={() => openReservationAccount(selectedReservationId)}
                    disabled={!reservations.length}
                  >
                    <FiDollarSign size={14} aria-hidden="true" />
                    {t("View reservation")}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="rav-filterBtn"
                  onClick={clearAllColumnFilters}
                  disabled={!hasColumnFilters}
                >
                  {t("Clear table filters")}
                </button>
              </div>
            </div>

            <div className="rav-table">
              <div className="rav-tableHeader">
                  <ColumnFilterCell
                    t={t}
                    label={t("Date")}
                    filterKey="date"
                    active={isColumnFilterActive(columnFilters, "date")}
                    isOpen={openFilterKey === "date"}
                    onToggle={toggleFilterPanel}
                    onClear={clearColumnFilter}
                  >
                    <DateFilterPanel
                      t={t}
                      filter={columnFilters.date}
                      options={dateOptions}
                      onToggle={(value) => toggleColumnSelection("date", value)}
                      onPatch={(patch) => patchColumnFilter("date", patch)}
                    />
                  </ColumnFilterCell>
                  <ColumnFilterCell
                    t={t}
                    label={t("Res.")}
                    filterKey="reservation"
                    active={isColumnFilterActive(columnFilters, "reservation")}
                    isOpen={openFilterKey === "reservation"}
                    onToggle={toggleFilterPanel}
                    onClear={clearColumnFilter}
                  >
                    <SelectionFilterPanel
                      t={t}
                      filter={columnFilters.reservation}
                      options={reservationOptions}
                      onToggle={(value) => toggleColumnSelection("reservation", value)}
                    />
                  </ColumnFilterCell>
                  {LEDGER_FAMILIES.map((family) => (
                    <React.Fragment key={family.key}>
                      <ColumnFilterCell
                        t={t}
                        label={t(family.label)}
                        filterKey={getConceptFilterKey(family.key)}
                        active={isColumnFilterActive(columnFilters, getConceptFilterKey(family.key))}
                        isOpen={openFilterKey === getConceptFilterKey(family.key)}
                        alignRight={family.key === "payments"}
                        onToggle={toggleFilterPanel}
                        onClear={clearColumnFilter}
                      >
                        <ConceptFilterPanel
                          t={t}
                          filter={columnFilters[getConceptFilterKey(family.key)]}
                          options={conceptOptionsByFamily[family.key] || []}
                          onToggle={(value) => toggleColumnSelection(getConceptFilterKey(family.key), value)}
                          onPatch={(patch) => patchColumnFilter(getConceptFilterKey(family.key), patch)}
                        />
                      </ColumnFilterCell>
                      <ColumnFilterCell
                        t={t}
                        label={t("State")}
                        filterKey={getStateFilterKey(family.key)}
                        active={isColumnFilterActive(columnFilters, getStateFilterKey(family.key))}
                        isOpen={openFilterKey === getStateFilterKey(family.key)}
                        alignRight={family.key === "payments"}
                        onToggle={toggleFilterPanel}
                        onClear={clearColumnFilter}
                      >
                        <SelectionFilterPanel
                          t={t}
                          filter={columnFilters[getStateFilterKey(family.key)]}
                          options={stateOptionsByFamily[family.key] || []}
                          onToggle={(value) => toggleColumnSelection(getStateFilterKey(family.key), value)}
                        />
                      </ColumnFilterCell>
                      <ColumnFilterCell
                        t={t}
                        label={t("Amount")}
                        filterKey={getAmountFilterKey(family.key)}
                        active={isColumnFilterActive(columnFilters, getAmountFilterKey(family.key))}
                        isOpen={openFilterKey === getAmountFilterKey(family.key)}
                        alignRight={family.key === "payments"}
                        onToggle={toggleFilterPanel}
                        onClear={clearColumnFilter}
                      >
                        <AmountFilterPanel
                          t={t}
                          filter={columnFilters[getAmountFilterKey(family.key)]}
                          options={amountOptionsByFamily[family.key] || []}
                          onToggle={(value) => toggleColumnSelection(getAmountFilterKey(family.key), value)}
                          onPatch={(patch) => patchColumnFilter(getAmountFilterKey(family.key), patch)}
                          minorUnits={displayMinorUnits}
                        />
                      </ColumnFilterCell>
                    </React.Fragment>
                  ))}
                </div>

              {filteredLines.map((line) => (
                <LedgerRow
                  t={t}
                  key={line.id}
                  line={line}
                  currency={currency}
                  reservationsById={reservationsById}
                  formatDisplayMoney={formatDisplayMoney}
                  formatDisplaySignedMoney={formatDisplaySignedMoney}
                />
              ))}
              {!filteredLines.length ? (
                <div className="rav-empty rav-empty--table">{t("No lines match the current filters.")}</div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ReservationAccountView;
